import { useTranslation } from "react-i18next";
import { EurAdvisoryBanner } from "./EurAdvisoryBanner";
import {
  formatEurPayment,
  formatKcPayment,
  type PaymentDrillDownSpz,
} from "../../schemas/payment";

/**
 * UC-004 PaymentDrillDown — per-SPZ kalkulace + transaction breakdown
 * (Phase 19, REQ-UI-009 + REQ-BIZ-018/019 + princip #3 fair-split transparency).
 *
 * Renderuje "Z čeho se částka skládá":
 *   - Per-SPZ řádky (plate, count, amount_kc, amount_eur paralelně)
 *   - Disclosed transactions sub-table per SPZ
 *
 * EUR advisory:
 *   - Pokud `eurAdvisoryFlag === true` → EurAdvisoryBanner nad tabulkou
 *     (REQ-BIZ-018 — EUR transakce v drill-downu, ale QR vždy `CC:CZK`).
 *
 * Mobile-first responsive: na < md breakpoint se zobrazí jako card stack,
 * na ≥ md jako table (CSS handles, není podmíněně rendered).
 */

export interface PaymentDrillDownProps {
  amountKc: string;
  amountEur: string;
  eurAdvisoryFlag: boolean;
  billingGroupCode?: string;
  drillDown: PaymentDrillDownSpz[];
}

export function PaymentDrillDown({
  amountKc,
  amountEur,
  eurAdvisoryFlag,
  billingGroupCode,
  drillDown,
}: PaymentDrillDownProps) {
  const { t } = useTranslation("tenant");
  return (
    <section className="flex flex-col gap-4" data-testid="payment-drilldown">
      <EurAdvisoryBanner
        visible={eurAdvisoryFlag}
        billingGroupCode={billingGroupCode}
      />
      <header>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-tertiary">{t("payment.amount")}</span>
          <strong className="text-2xl font-bold text-primary" data-testid="payment-drilldown-total">
            {formatKcPayment(amountKc)}
          </strong>
          {eurAdvisoryFlag && (
            <span className="text-sm text-tertiary" data-testid="payment-drilldown-eur">
              {formatEurPayment(amountEur)}
            </span>
          )}
        </div>
      </header>
      <table className="w-full border-collapse text-left text-sm text-primary" data-testid="payment-drilldown-table">
        <caption className="sr-only">{t("payment.drilldown")}</caption>
        <thead>
          <tr className="border-b border-secondary text-xs font-semibold text-tertiary">
            <th scope="col" className="px-2 py-2">{t("payment.spzColumn")}</th>
            <th scope="col" className="px-2 py-2">{t("payment.litersColumn")}</th>
            <th scope="col" className="px-2 py-2">{t("payment.transactionsColumn")}</th>
            <th scope="col" className="px-2 py-2">{t("payment.amountColumn")}</th>
          </tr>
        </thead>
        <tbody>
          {drillDown.map((spz) => (
            <tr
              key={spz.fuel_card_id}
              className="border-b border-secondary"
              data-testid={`payment-drilldown-row-${spz.plate_text}`}
            >
              <td className="px-2 py-2">{spz.plate_text}</td>
              <td className="px-2 py-2">
                {spz.transactions.reduce(
                  (sum, t) => sum + (t.liters ? Number(t.liters) : 0),
                  0,
                ).toLocaleString("cs-CZ", { maximumFractionDigits: 2 })} L
              </td>
              <td className="px-2 py-2">{spz.transaction_count}</td>
              <td className="px-2 py-2">{formatKcPayment(spz.amount_kc)}</td>
            </tr>
          ))}
          {drillDown.length === 0 && (
            <tr>
              <td colSpan={4} className="px-2 py-4 text-center text-tertiary">
                —
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
